<template>
  <div class="transcription-panel-wrapper" :class="{ 'collapsed': isCollapsed }">
    <!-- Collapse/Expand Toggle Button -->
    <div class="collapse-btn-wrapper">
      <button
        class="collapse-expand-button"
        :title="isCollapsed ? 'Expand panel' : 'Collapse panel'"
        @click="toggleCollapse"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          :style="{ transform: isCollapsed ? 'rotate(180deg)' : 'none' }"
        >
          <path d="M17.59 6L19 7.41L14.42 12L19 16.59L17.59 18L11.59 12L17.59 6Z" fill="#374957"/>
          <path d="M11 6L12.41 7.41L7.83 12L12.41 16.59L11 18L5 12L11 6Z" fill="#374957"/>
        </svg>
      </button>
    </div>

    <!-- Main Panel Content -->
    <div class="transcription-panel">
      <!-- Panel Content -->
      <div class="panel-content">
        <!-- Play Recording Button -->
        <div class="play-recording-section">
          <div
            class="play-recording-split"
          >
            <!-- Left: Play button -->
            <button
              class="play-part"
              :class="{ 'hovered': isPlayHovered }"
              :disabled="audioLoading"
              @click="togglePlayback"
              @mouseenter="isPlayHovered = true"
              @mouseleave="isPlayHovered = false"
            >
              <!-- Loading spinner -->
              <svg v-if="audioLoading" class="audio-loading-spinner" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="8" stroke="#004879" stroke-width="2" stroke-linecap="round" stroke-dasharray="40 60" />
              </svg>
              <svg v-else-if="!isPlaying" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 5V19L19 12L8 5Z" fill="#004879" stroke="#004879" stroke-width="1.5" stroke-linejoin="round"/>
              </svg>
              <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="5" width="4" height="14" rx="1" fill="#004879"/>
                <rect x="14" y="5" width="4" height="14" rx="1" fill="#004879"/>
              </svg>
              <span class="play-text">{{ audioLoading ? 'Loading...' : isPlaying ? 'Pause' : 'Play Recording' }}</span>
              <span v-if="hasAudioDuration" class="play-text">{{ formattedDuration }}</span>

              <!-- Tooltip: Playing Recording X -->
              <div v-if="isPlayHovered && !showSessionDropdown" class="split-tooltip play-tooltip">
                <div class="tooltip-arrow"></div>
                <div class="tooltip-body">Playing Recording {{ sessions.length - selectedSessionIndex }}</div>
              </div>
            </button>

            <!-- Right: Dropdown arrow (only when multiple sessions) -->
            <button
              v-if="sessions.length > 1"
              class="dropdown-part"
              :class="{ 'hovered': isDropdownHovered || showSessionDropdown }"
              @click.stop="toggleSessionDropdown"
              @mouseenter="isDropdownHovered = true"
              @mouseleave="isDropdownHovered = false"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="#004879"/>
              </svg>

              <!-- Tooltip: Switch to another recording -->
              <div v-if="isDropdownHovered && !showSessionDropdown" class="split-tooltip dropdown-tooltip">
                <div class="tooltip-arrow"></div>
                <div class="tooltip-body">Switch to another recording</div>
              </div>
            </button>

            <!-- Divider between left/right when hovered -->
            <div
              v-if="(isPlayHovered || isDropdownHovered || showSessionDropdown) && sessions.length > 1"
              class="split-divider"
            ></div>
          </div>

          <!-- Session Dropdown -->
          <div v-if="showSessionDropdown" class="session-dropdown">
            <div
              v-for="reverseIndex in sessions.length"
              :key="sessions[sessions.length - reverseIndex].id"
              class="session-item"
              :class="{ 'selected': (sessions.length - reverseIndex) === selectedSessionIndex }"
              @click="selectSession(sessions.length - reverseIndex)"
            >
              Recording {{ reverseIndex }}
            </div>
          </div>

          <!-- Click-outside overlay to close dropdown -->
          <div v-if="showSessionDropdown" class="dropdown-overlay" @click="showSessionDropdown = false"></div>

          <audio
            ref="audioPlayer"
            :src="recordingUrl"
            preload="metadata"
            @ended="onAudioEnded"
            @pause="isPlaying = false"
            @play="isPlaying = true"
            @loadedmetadata="onLoadedMetadata"
            @timeupdate="onTimeUpdate"
            @error="onAudioError"
          />
        </div>

        <!-- Tabs -->
        <div class="tabs-container">
          <button
            :class="['tab', { 'tab-active': activeTab === 'transcription' }]"
            @click="activeTab = 'transcription'"
          >
            Transcription
          </button>
          <button
            :class="['tab', { 'tab-active': activeTab === 'summary' }]"
            @click="switchToSummary"
          >
            AI-generated Summary
          </button>
        </div>

        <!-- Content Area -->
        <div class="content-area">
          <div class="content-scroll">
            <!-- Loading state when switching recordings -->
            <div v-if="switching" class="loading-container">
              <LoadingSpinner />
              <span class="loading-text">Loading transcription...</span>
            </div>
            <!-- Loading state for summary tab -->
            <div v-else-if="activeTab === 'summary' && loading" class="loading-container">
              <LoadingSpinner />
              <span class="loading-text">Regenerating summary...</span>
            </div>
            <div v-else-if="activeTab === 'transcription'" class="content-text" v-html="formattedTranscription"></div>
            <div v-else class="content-text" v-html="formattedSummary"></div>
          </div>
        </div>

        <!-- Action Buttons - only shown in draft mode -->
        <div v-if="isDraft" class="action-buttons flex items-center justify-between gap-x-3">
          <button class="btn-insert" :disabled="isLocked" @click="$emit('insert', activeTab === 'transcription' ? transcription : summary)">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="#81B0E1"/>
            </svg>
            Insert
          </button>
          <button class="btn-regenerate" :disabled="loading || isLocked" @click="handleRegenerate">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="#374957"/>
            </svg>
            Regenerate
          </button>
          <button class="btn-manage" :disabled="loading || isLocked" title="Manage Prompts" @click="$emit('manage-prompts')">
            <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.18 11C8.73 11 9.18 11.45 9.18 12C9.18 12.55 8.73 13 8.18 13C7.63 13 7.18 12.55 7.18 12C7.18 11.45 7.63 11 8.18 11ZM8.18 9C6.52 9 5.18 10.34 5.18 12C5.18 13.66 6.52 15 8.18 15C9.84 15 11.18 13.66 11.18 12C11.18 10.34 9.84 9 8.18 9ZM16.68 7L17.77 4.59L20.18 3.5L17.77 2.41L16.68 0L15.59 2.41L13.18 3.5L15.59 4.59L16.68 7ZM19.46 10.72L18.68 9L17.9 10.72L16.18 11.5L17.9 12.28L18.68 14L19.46 12.28L21.18 11.5L19.46 10.72ZM14.43 12C14.43 11.88 14.43 11.75 14.42 11.63L16.36 10.16L13.86 5.83L11.62 6.77C11.42 6.64 11.2 6.51 10.98 6.4L10.68 4H5.68L5.38 6.41C5.16 6.52 4.95 6.65 4.74 6.78L2.5 5.83L0 10.16L1.94 11.63C1.93 11.75 1.93 11.88 1.93 12C1.93 12.12 1.93 12.25 1.94 12.37L0 13.84L2.5 18.17L4.74 17.23C4.94 17.36 5.16 17.49 5.38 17.6L5.68 20H10.68L10.98 17.59C11.2 17.48 11.41 17.36 11.62 17.22L13.86 18.16L16.36 13.83L14.42 12.36C14.43 12.25 14.43 12.12 14.43 12ZM13.01 15.64L11.28 14.91C10.72 15.51 9.98 15.95 9.15 16.14L8.91 18H7.45L7.22 16.14C6.39 15.95 5.65 15.51 5.09 14.91L3.36 15.64L2.63 14.37L4.12 13.24C4 12.85 3.94 12.44 3.94 12.01C3.94 11.58 4 11.17 4.12 10.78L2.63 9.65L3.36 8.38L5.09 9.11C5.65 8.51 6.39 8.07 7.22 7.88L7.45 6H8.92L9.15 7.86C9.98 8.05 10.72 8.49 11.28 9.09L13.01 8.36L13.74 9.63L12.25 10.76C12.37 11.15 12.43 11.56 12.43 11.99C12.43 12.42 12.37 12.83 12.25 13.22L13.74 14.35L13.01 15.64Z" fill="#2C4FC9"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import LoadingSpinner from './LoadingSpinner.vue';

const props = defineProps({
  transcription: {
    type: String,
    default: ''
  },
  summary: {
    type: String,
    default: ''
  },
  duration: {
    type: Number,
    default: 0
  },
  recordingUrl: {
    type: String,
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  },
  switching: {
    type: Boolean,
    default: false
  },
  isDraft: {
    type: Boolean,
    default: true
  },
  audioLoading: {
    type: Boolean,
    default: false
  },
  isLocked: {
    type: Boolean,
    default: false
  },
  sessions: {
    type: Array,
    default: () => []
  },
  selectedSessionIndex: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['insert', 'regenerate', 'requestAudio', 'tabChange', 'manage-prompts', 'select-session']);

// Collapse state
const isCollapsed = ref(false);

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
};

// Split button hover/dropdown state
const isPlayHovered = ref(false);
const isDropdownHovered = ref(false);
const showSessionDropdown = ref(false);

const toggleSessionDropdown = () => {
  showSessionDropdown.value = !showSessionDropdown.value;
};

const selectSession = (index) => {
  showSessionDropdown.value = false;
  emit('select-session', index);
};

const handleRegenerate = () => {
  // Switch to summary tab when regenerating
  activeTab.value = 'summary';
  emit('regenerate');
};

const activeTab = ref('transcription');

const switchToSummary = () => {
  activeTab.value = 'summary';
  emit('tabChange', 'summary');
};
const audioPlayer = ref(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const audioDuration = ref(0);

const pendingAutoPlay = ref(false);

const togglePlayback = () => {
  if (!audioPlayer.value) return;

  // If loading audio, ignore clicks
  if (props.audioLoading) return;

  // If no audio URL yet, request it from the parent and auto-play when ready
  if (!props.recordingUrl) {
    pendingAutoPlay.value = true;
    emit('requestAudio');
    return;
  }

  if (isPlaying.value) {
    audioPlayer.value.pause();
  } else {
    audioPlayer.value.play();
  }
};

// Reset audio state when switching sessions
watch(() => props.switching, (isSwitching) => {
  if (isSwitching) {
    isPlaying.value = false;
    currentTime.value = 0;
    audioDuration.value = 0;
  }
});

// Auto-play once recordingUrl arrives after user clicked play
watch(() => props.recordingUrl, (newUrl) => {
  if (newUrl && pendingAutoPlay.value) {
    pendingAutoPlay.value = false;
    nextTick(() => {
      audioPlayer.value?.play();
    });
  }
});

const onLoadedMetadata = () => {
  if (audioPlayer.value) {
    audioDuration.value = audioPlayer.value.duration || 0;
  }
};

const onTimeUpdate = () => {
  if (audioPlayer.value) {
    currentTime.value = audioPlayer.value.currentTime || 0;
  }
};

const onAudioEnded = () => {
  isPlaying.value = false;
  currentTime.value = 0;
};

const onAudioError = (event) => {
  console.error('Audio playback error:', event);
  const audio = audioPlayer.value;
  if (audio && audio.error) {
    console.error('Audio error code:', audio.error.code, 'message:', audio.error.message);
  }
  isPlaying.value = false;
};

const formatTime = (seconds) => {
  // Handle invalid values (Infinity, NaN, null, undefined)
  if (!seconds || !isFinite(seconds) || isNaN(seconds)) {
    return '00:00';
  }
  
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// Whether we have a real audio duration to display
// Only show if audio has been loaded (recordingUrl exists) or is playing
const hasAudioDuration = computed(() => {
  if (!props.recordingUrl && !isPlaying.value && currentTime.value === 0) return false;
  return (audioDuration.value && isFinite(audioDuration.value) && audioDuration.value > 0)
    || (props.duration > 0)
    || isPlaying.value
    || currentTime.value > 0;
});

const formattedDuration = computed(() => {
  const total = (audioDuration.value && isFinite(audioDuration.value))
    ? audioDuration.value
    : (props.duration || 0);

  if (isPlaying.value || currentTime.value > 0) {
    return `${formatTime(currentTime.value)} / ${formatTime(total)}`;
  }
  return formatTime(total);
});

const formattedTranscription = computed(() => props.transcription);
const formattedSummary = computed(() => props.summary);
</script>

<style scoped>
/* Wrapper for panel and toggle button */
.transcription-panel-wrapper {
  position: relative;
  flex-shrink: 0;
  align-self: stretch;
  width: 350px;
  transition: width 0.5s ease-in-out;
}

.transcription-panel-wrapper.collapsed {
  width: 20px;
}

.transcription-panel-wrapper.collapsed::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 20px;
  background: #F9FBFD;
  border-radius: 5px;
}

/* Collapse Button Wrapper — positioned at right edge of wrapper */
.collapse-btn-wrapper {
  position: absolute;
  right: -12px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 10;
  transition: right 0.5s ease-in-out;
}

/* Collapse/Expand Button - matches my-patients design */
.collapse-expand-button {
  pointer-events: auto;
  display: flex;
  padding: 24px 2px;
  align-items: center;
  gap: 12px;
  border-radius: 4px;
  background: #F2F2F2;
  border: none;
  cursor: pointer;
}

.collapse-expand-button:hover {
  background: #E5E5E5;
}

.collapse-expand-button svg {
  transition: transform 0.5s ease-in-out;
}

/* Panel Content wrapper */
.panel-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.transcription-panel {
  display: flex;
  flex-direction: column;
  width: 350px;
  min-height: 400px;
  height: 100%;
  flex-shrink: 0;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 1px 15px rgba(164, 164, 164, 0.25);
  overflow: hidden;
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;
}

/* Slide the panel out to the left when collapsed */
.transcription-panel-wrapper.collapsed .transcription-panel {
  transform: translateX(-340px);
  opacity: 0;
  pointer-events: none;
}

/* Play Recording Section */
.play-recording-section {
  flex-shrink: 0;
  padding: 12px;
  position: relative;
}

/* Split button container */
.play-recording-split {
  display: flex;
  align-items: center;
  width: 100%;
  height: 32px;
  background: #ffffff;
  border-radius: 4px;
  position: relative;
  border: 1px solid #004879;
  box-shadow: 0px 1px 3px rgba(74, 74, 74, 0.25);
}

/* Left play part */
.play-part {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 4px 12px;
  background: transparent;
  border: none;
  border-radius: 4px 0 0 4px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  position: relative;
  overflow: hidden;
}

.play-part.hovered {
  background: rgba(0, 72, 121, 0.08);
}

.play-part:disabled {
  opacity: 0.7;
}

/* Right dropdown arrow part */
.dropdown-part {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 100%;
  padding: 4px;
  background: transparent;
  border: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  transition: background-color 0.15s ease;
  position: relative;
  flex-shrink: 0;
}

.dropdown-part.hovered {
  background: rgba(0, 72, 121, 0.08);
}

/* Vertical divider between play and dropdown */
.split-divider {
  position: absolute;
  right: 48px;
  top: 4px;
  bottom: 4px;
  width: 1px;
  background: #004879;
  pointer-events: none;
}

.play-text {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #1E4670;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Tooltips */
.split-tooltip {
  position: absolute;
  top: calc(100% + 6px);
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
}

.play-tooltip {
  left: 28px;
  align-items: flex-start;
}

.dropdown-tooltip {
  right: -8px;
  align-items: flex-end;
}

.tooltip-arrow {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #666;
}

.tooltip-body {
  background: #666;
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  padding: 6px 16px;
  border-radius: 2px;
  white-space: nowrap;
}

/* Session Dropdown */
.session-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.1);
  z-index: 20;
  padding: 10px 0;
}

.session-item {
  padding: 4px 22px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: #333;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.session-item:hover {
  background: #f5f9fc;
}

.session-item.selected {
  font-weight: 500;
  color: #004879;
}

/* Click-outside overlay */
.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 19;
}

/* Tabs */
.tabs-container {
  display: flex;
  flex-shrink: 0;
  background: #ffffff;
  padding: 8px 8px 0;
  border-radius: 5px 5px 0 0;
}

.tab {
  flex: 1;
  padding: 4px 16px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  text-align: center;
  color: #666666;
  background: #ffffff;
  border: none;
  border-bottom: 1px solid #e6e6e6;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.tab:hover {
  color: #004879;
}

.tab-active {
  color: #004879;
  font-weight: 500;
  border-bottom: 2px solid #004879;
}

/* Content Area */
.content-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f9fbfd;
  overflow: hidden;
  min-height: 0;
}

.content-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 10px 20px;
}

.content-text {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333333;
  word-wrap: break-word;
}

.content-text :deep(p) {
  margin: 0 0 8px 0;
}

.content-text :deep(strong) {
  font-weight: 600;
}

.content-text :deep(ul),
.content-text :deep(ol) {
  margin: 0 0 8px 21px;
  padding: 0;
}

.content-text :deep(li) {
  margin-bottom: 2px;
}

.content-text :deep(h1),
.content-text :deep(h2),
.content-text :deep(h3),
.content-text :deep(h4),
.content-text :deep(h5),
.content-text :deep(h6) {
  font-size: inherit;
  font-weight: 700;
  line-height: 20px;
  margin: 0 0 4px 0;
}

.content-text :deep(blockquote) {
  margin: 0 0 8px 0;
  padding: 4px 12px;
  border-left: 3px solid #004879;
  color: #555555;
  background: #f5f9fc;
}

.content-text :deep(code) {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  background: #f0f0f0;
  padding: 1px 4px;
  border-radius: 3px;
}

.content-text :deep(pre) {
  margin: 0 0 8px 0;
  padding: 8px 12px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow-x: auto;
}

.content-text :deep(pre code) {
  background: none;
  padding: 0;
}

.content-text :deep(hr) {
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 8px 0;
}

.content-text :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 8px 0;
  font-size: 13px;
}

.content-text :deep(th),
.content-text :deep(td) {
  border: 1px solid #e0e0e0;
  padding: 4px 8px;
  text-align: left;
}

.content-text :deep(th) {
  background: #f5f5f5;
  font-weight: 600;
}

/* Action Buttons */
.action-buttons {
 
  padding: 10px 20px;
  min-height: 65px;
  background: #ffffff;
  box-shadow: 0px -4px 7px rgba(0, 0, 0, 0.05);
  border-radius: 0 0 5px 5px;
}

.btn-insert {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  max-width: 135px;
  height: 32px;
  padding: 4px;
  background: #ffffff;
  border: 2px solid #81B0E1;
  border-radius: 4px;
  box-shadow: 0px 1px 3px rgba(74, 74, 74, 0.25);
  font-family: 'IBM Plex Sans Hebrew', 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #5587BC;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.btn-insert:hover {
  background: #f5f9fc;
  border-color: #5587BC;
}

.btn-regenerate {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  height: 32px;
  padding: 4px;
  background: #ffffff;
  border: 1px solid #1E4670;
  border-radius: 4px;
  box-shadow: 0px 1px 3px rgba(74, 74, 74, 0.25);
  font-family: 'IBM Plex Sans Hebrew', 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #1E4670;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-regenerate:hover {
  background: #f5f9fc;
}

/* Scrollbar Styling */
.content-scroll::-webkit-scrollbar {
  width: 5px;
}

.content-scroll::-webkit-scrollbar-track {
  background: #e0e0e0;
  border-radius: 13px;
}

.content-scroll::-webkit-scrollbar-thumb {
  background: #c6c6c6;
  border-radius: 13px;
}

.content-scroll::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  gap: 46px;
}

.loading-container :deep(.spinner-container) {
  height: auto;
  background-color: transparent;
}

.loading-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #666666;
}

.btn-insert:disabled,
.btn-regenerate:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-manage {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 4px;
  background: #ffffff;
  border: 1px solid #2C4FC9;
  border-radius: 4px;
  box-shadow: 0px 1px 3px rgba(74, 74, 74, 0.25);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-manage:hover {
  background: #f0f4ff;
}

.btn-manage:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.audio-loading-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

</style>
